import Message, { MessageReplyTo } from "types/Message";
import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Download,
  Mail,
  MessageCircle,
  Link as LinkIcon,
  Check,
  AlertTriangle,
  Info,
  ThumbsUp,
  ThumbsDown,
  Clock,
} from "lucide-react";
import { MessageAvatar } from "./MessageAvatar";
import { WhatsAppAudioPlayer } from "./WhatsAppAudioPlayer";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Modal } from "./ui/Modal";
import { TextArea } from "./ui/TextArea";
import { Button } from "./ui/Button";
import { isPresignedUrl, regenerateFileUrl } from "../apiClient";
import { useAuth } from "context/AuthContext";
import { useMessageFeedback } from "../hooks/useMessageFeedback";
import { formatMessageTimestamp } from "utils/dateFormat";
import { LinkifiedText } from "utils/linkifyText";
import {
  getMessageBubbleClassName,
  getMessageBubbleHighlightClassName,
  getReplyQuoteClassName,
} from "utils/messageStyles";

interface MessageBalloonProps {
  message: Message;
  highlighted?: boolean;
  messageRef?: (el: HTMLDivElement | null) => void;
  contactId?: number;
}

export function MessageBalloon({
  message,
  highlighted = false,
  messageRef,
  contactId,
}: MessageBalloonProps) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const fileFallbackLabel = tr("Archivo adjunto", "File attachment");
  const fileExtFallbackLabel = tr("ARCHIVO", "FILE");

  const [copied, setCopied] = useState(false);
  const [fileUrl, setFileUrl] = useState(message.fileUrl);
  const [regeneratingUrl, setRegeneratingUrl] = useState(false);
  const audioUrlsAlreadyRetried = useRef<Set<string>>(new Set());
  const [searchParams] = useSearchParams();
  const [showBadAnswerModal, setShowBadAnswerModal] = useState(false);
  const [badAnswerReason, setBadAnswerReason] = useState("");

  const canRateAnswer = message.type === "assistant";
  const { feedback, submitFeedback, clearFeedback, submitting } =
    useMessageFeedback(message.id, message.feedback);
  const { display: timeDisplay, title: timeTitle } = formatMessageTimestamp(
    message.sentAt,
    locale
  );
  const scheduledSendLabel = (() => {
    if (message.type !== "deferred" || !message.scheduledSendAt) return null;
    const scheduledDate = new Date(message.scheduledSendAt);
    return scheduledDate.toLocaleString(locale === "es" ? "es-ES" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  })();
  const bubbleSurfaceClass = highlighted
    ? getMessageBubbleHighlightClassName(message.type)
    : getMessageBubbleClassName(message.type);

  // Handle expired URLs
  const handleAudioError = async () => {
    const currentUrl = fileUrl || message.fileUrl;
    if (!currentUrl || regeneratingUrl) return;

    // Only an expired presigned URL is worth regenerating. Without this guard a
    // permanent failure (unsupported codec, 403, offline) would loop forever:
    // every new URL remounts the player, which errors again.
    if (!isPresignedUrl(currentUrl)) return;
    if (audioUrlsAlreadyRetried.current.has(currentUrl)) return;

    audioUrlsAlreadyRetried.current.add(currentUrl);
    await regenerateAndUpdateUrl(currentUrl);
  };

  const handleImageError = async (
    e: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    const img = e.currentTarget;
    const currentUrl = img.src;

    if (isPresignedUrl(currentUrl)) {
      // Try to fetch to check if URL is expired
      try {
        await fetch(currentUrl, { method: "HEAD", mode: "no-cors" });
        // If we can't check (CORS), try regenerating anyway for presigned URLs
        await regenerateAndUpdateUrl(currentUrl);
      } catch (err) {
        // CORS error or network error - try regenerating for presigned URLs
        await regenerateAndUpdateUrl(currentUrl);
      }
    }
  };

  const regenerateAndUpdateUrl = async (oldUrl: string | null) => {
    if (!oldUrl || regeneratingUrl) return;

    setRegeneratingUrl(true);
    try {
      const newUrl = await regenerateFileUrl(oldUrl);
      if (newUrl) {
        setFileUrl(newUrl);
      }
    } catch (err) {
      console.error("Failed to regenerate file URL:", err);
    } finally {
      setRegeneratingUrl(false);
    }
  };

  const handleDownloadClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    const currentUrl = fileUrl || message.fileUrl;
    if (!currentUrl) {
      e.preventDefault();
      return;
    }

    if (isPresignedUrl(currentUrl)) {
      // Prevent default behavior while we regenerate
      e.preventDefault();
      setRegeneratingUrl(true);

      try {
        // Try to regenerate before download to ensure URL is fresh
        const newUrl = await regenerateFileUrl(currentUrl);
        if (newUrl) {
          setFileUrl(newUrl);
          // Programmatically trigger download with new URL
          const link = document.createElement("a");
          link.href = newUrl;
          link.download =
            message.fileName ?? getFileName(newUrl, fileFallbackLabel);
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          // If regeneration failed, try with original URL
          const link = document.createElement("a");
          link.href = currentUrl;
          link.download =
            message.fileName ?? getFileName(currentUrl, fileFallbackLabel);
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err) {
        console.error("Failed to regenerate URL for download:", err);
        // Fallback: try with original URL
        const link = document.createElement("a");
        link.href = currentUrl;
        link.download =
          message.fileName ?? getFileName(currentUrl, fileFallbackLabel);
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        setRegeneratingUrl(false);
      }
    }
    // If not presigned URL, let default behavior proceed (no preventDefault)
  };

  const generateMessageUrl = () => {
    const url = new URL(window.location.href);
    // Preserve condo ID if present
    const condoId = searchParams.get("condo");
    if (condoId) {
      url.searchParams.set("condo", condoId);
    }
    if (contactId) {
      url.searchParams.set("contact", contactId.toString());
    }
    url.searchParams.set("message-id", String(message.id));
    return url.toString();
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = generateMessageUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const handleThumbsUp = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (submitting) return;
    try {
      if (feedback === "up") {
        await clearFeedback();
      } else {
        await submitFeedback("up");
      }
    } catch (err) {
      console.error("Failed to submit positive feedback:", err);
    }
  };

  const handleThumbsDown = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (submitting) return;
    if (feedback === "down") {
      try {
        await clearFeedback();
      } catch (err) {
        console.error("Failed to clear negative feedback:", err);
      }
      return;
    }
    setBadAnswerReason("");
    setShowBadAnswerModal(true);
  };

  const handleSubmitBadAnswer = async () => {
    const reason = badAnswerReason.trim();
    if (!reason) return;
    try {
      await submitFeedback("down", reason);
      setShowBadAnswerModal(false);
      setBadAnswerReason("");
    } catch (err) {
      console.error("Failed to submit negative feedback:", err);
    }
  };
  const isRightAligned = ["user", "manager", "system"].includes(message.type);
  const isInternal = ["manager", "manager-response"].includes(message.type);
  const managerName = message.autoResponseTargetName || "manager";
  const autoResponseFooter = (() => {
    if (!message.autoResponseTargetName) {
      return null;
    }

    if (message.type === "user") {
      return (
        <span>
          {tr(`Mensaje dirigido a ${managerName}`, `Message directed to ${managerName}`)}
        </span>
      );
    }

    if (message.type === "operator") {
      return (
        <span>
          {tr("Respuesta ", "Response ")}
          <strong>{tr("manual", "manual")}</strong>
          {tr(` de ${managerName}`, ` from ${managerName}`)}
        </span>
      );
    }

    if (message.type === "assistant") {
      return (
        <span>
          {tr("Respuesta ", "Response ")}
          <strong>{tr("automática", "automatic")}</strong>
          {tr(` de ${managerName}`, ` from ${managerName}`)}
        </span>
      );
    }

    return null;
  })();

  const deferredFooter =
    message.type === "deferred" ? (
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {scheduledSendLabel
          ? tr(
              `No enviado · Se enviará el ${scheduledSendLabel}`,
              `Not sent · Will be sent on ${scheduledSendLabel}`,
            )
          : tr("No enviado · Programado", "Not sent · Scheduled")}
      </span>
    ) : null;

  // No animation needed - yellow border is enough

  return (
    <div
      ref={messageRef}
      className={`flex ${
        isRightAligned ? "justify-end" : "justify-start"
      } w-full transition-all duration-300`}
      id={`message-${message.listKey}`}
    >
      {!isRightAligned && (
        <div className="flex-shrink-0 mr-1 lg:mr-2 mt-1">
          <MessageAvatar messageType={message.type} size="sm" />
        </div>
      )}
      <div
        className={`chat-message group p-2.5 lg:p-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap max-w-[88%] lg:max-w-[85%] w-fit relative transition-all duration-300 min-w-[100px] ${
          isInternal ? "italic" : ""
        } ${bubbleSurfaceClass} ${
          highlighted ? "ring-2 ring-amber-400/80 ring-offset-1 shadow-md" : ""
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 min-w-0 flex-1 text-gray-500">
            {getChannelIcon(message.type, message.channel)}
            {message.channel === "mail" && message.subject ? (
              <span className="text-xs truncate" title={message.subject}>
                {message.subject}
              </span>
            ) : null}
            {getWappStatusIndicator(message)}
            {getReplyWindowSubstituteIndicator(message, tr)}
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <span
              className="text-[10px] lg:text-xs text-gray-500 whitespace-nowrap tabular-nums"
              title={timeTitle}
            >
              {timeDisplay}
            </span>
            <div className="chat-message-actions flex items-center gap-0.5 ml-1">
              {canRateAnswer && (
                <>
                  <button
                    type="button"
                    onClick={handleThumbsUp}
                    disabled={submitting}
                    className={`flex items-center rounded p-1 transition-colors ${
                      feedback === "up"
                        ? "text-green-600 bg-green-50"
                        : "text-gray-400 hover:text-green-600 hover:bg-green-50/80"
                    } disabled:opacity-50`}
                    title={
                      feedback === "up"
                        ? tr("Quitar valoración", "Remove rating")
                        : tr("Buena respuesta", "Good answer")
                    }
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleThumbsDown}
                    disabled={submitting}
                    className={`flex items-center rounded p-1 transition-colors ${
                      feedback === "down"
                        ? "text-red-600 bg-red-50"
                        : "text-gray-400 hover:text-red-600 hover:bg-red-50/80"
                    } disabled:opacity-50`}
                    title={
                      feedback === "down"
                        ? tr("Quitar valoración", "Remove rating")
                        : tr("Mala respuesta", "Bad answer")
                    }
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {contactId && message.type !== "deferred" && (
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="flex items-center rounded p-1 text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors"
                  title={tr(
                    "Copiar enlace a este mensaje",
                    "Copy link to this message",
                  )}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <LinkIcon className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
        {message.replyTo ? (
          <MessageReplyQuote replyTo={message.replyTo} tr={tr} />
        ) : null}
        <p className="break-words overflow-wrap-anywhere text-gray-900">
          <LinkifiedText text={message.content} />
        </p>
        {autoResponseFooter && (
          <div className="mt-2 text-[10px] lg:text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
            {autoResponseFooter}
          </div>
        )}
        {deferredFooter && (
          <div className="mt-2 text-[10px] lg:text-xs text-amber-800 bg-amber-100/80 border border-amber-200 rounded px-2 py-1">
            {deferredFooter}
          </div>
        )}

        {(() => {
          const currentFileUrl = fileUrl || message.fileUrl;
          if (!currentFileUrl || !currentFileUrl.trim()) {
            return null;
          }

          return isImageUrl(currentFileUrl) ? (
            <div className="mt-2 w-full">
              <img
                src={currentFileUrl}
                alt={
                  message.fileName ?? getFileName(currentFileUrl, fileFallbackLabel)
                }
                className="rounded-md w-full h-auto max-w-full object-contain"
                style={{ maxHeight: "300px" }}
                onError={handleImageError}
              />
              {regeneratingUrl && (
                <div className="text-xs text-gray-500 mt-1">
                  {tr("Regenerando URL...", "Regenerating URL...")}
                </div>
              )}
            </div>
          ) : isAudioUrl(currentFileUrl) ? (
            <div className="mt-2 w-full">
              <div className="flex items-center gap-2 rounded-2xl bg-[#ebf7db] border border-[#d7eec0] pl-1.5 pr-2 py-1.5">
                <WhatsAppAudioPlayer
                  key={currentFileUrl}
                  src={currentFileUrl}
                  onError={handleAudioError}
                  tr={tr}
                />
              </div>
              {regeneratingUrl && (
                <div className="text-xs text-gray-500 mt-1">
                  {tr("Regenerando URL...", "Regenerating URL...")}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-1 lg:gap-2 bg-gray-100 rounded p-1 lg:p-2 max-w-full">
              <span className="font-semibold bg-gray-200 rounded px-1 lg:px-2 py-0.5 text-xs flex-shrink-0">
                {getFileExtension(
                  currentFileUrl,
                  fileExtFallbackLabel,
                  fileFallbackLabel
                )}
              </span>
              <span className="text-xs truncate flex-1">
                {message.fileName ??
                  getFileName(currentFileUrl, fileFallbackLabel)}
              </span>
              <a
                href={currentFileUrl}
                download={
                  message.fileName ??
                  getFileName(currentFileUrl, fileFallbackLabel)
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:underline flex-shrink-0"
                title={tr("Descargar", "Download")}
                onClick={handleDownloadClick}
              >
                <Download className="h-3 w-3 lg:h-4 lg:w-4" />
              </a>
              {regeneratingUrl && (
                <span className="text-xs text-gray-500">
                  {tr("Regenerando...", "Regenerating...")}
                </span>
              )}
            </div>
          );
        })()}
      </div>
      {isRightAligned && (
        <div className="flex-shrink-0 ml-1 lg:ml-2 mt-1">
          <MessageAvatar messageType={message.type} size="sm" />
        </div>
      )}

      <Modal
        isOpen={showBadAnswerModal}
        onClose={() => {
          setShowBadAnswerModal(false);
          setBadAnswerReason("");
        }}
        title={tr("¿Por qué es una mala respuesta?", "Why is this a bad answer?")}
      >
        <div className="space-y-4">
          <TextArea
            value={badAnswerReason}
            onChange={setBadAnswerReason}
            placeholder={tr(
              "Explica qué estuvo mal o cuál es la respuesta correcta...",
              "Explain what was wrong or what the correct answer should be...",
            )}
            rows={4}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBadAnswerModal(false);
                setBadAnswerReason("");
              }}
            >
              {tr("Cancelar", "Cancel")}
            </Button>
            <Button
              onClick={handleSubmitBadAnswer}
              disabled={!badAnswerReason.trim() || submitting}
            >
              {tr("Enviar", "Submit")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Helper to check if a URL is an image
function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url.split("?")[0]);
}

function isAudioUrl(url: string): boolean {
  return /\.(mp3|ogg|opus|wav|m4a|webm|aac|flac|weba)$/i.test(
    url.split("?")[0],
  );
}

// Helper to extract file name from URL
function getFileName(url: string, fallbackLabel: string): string {
  try {
    return decodeURIComponent(
      url.split("/").pop()?.split("?")[0] || fallbackLabel,
    );
  } catch {
    return fallbackLabel;
  }
}

// Helper to extract file extension from URL
function getFileExtension(
  url: string,
  fallbackExtensionLabel: string,
  fallbackFileNameLabel: string
): string {
  const name = getFileName(url, fallbackFileNameLabel);
  const match = name.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toUpperCase() : fallbackExtensionLabel;
}

function getChannelIcon(messageType: Message["type"], channel?: string) {
  if (
    messageType === "manager" ||
    messageType === "manager-response"
  ) {
    return null;
  }

  if (messageType === "deferred") {
    return <Clock className="h-3 w-3 lg:h-4 lg:w-4 text-amber-600" />;
  }

  if (channel === "fake") {
    return <WhatsAppIcon className="h-3 w-3 lg:h-4 lg:w-4 text-green-500" />;
  }
  if (channel === "panel") {
    return <MessageCircle className="h-3 w-3 lg:h-4 lg:w-4 text-accent" />;
  }
  if (channel === "mail") {
    return <Mail className="h-3 w-3 lg:h-4 lg:w-4 text-blue-600" />;
  }
  return <MessageCircle className="h-3 w-3 lg:h-4 lg:w-4 text-gray-500" />;
}

function getWappStatusIndicator(message: Message) {
  // Only show status for WhatsApp messages (channel = "wapp") sent by user/manager
  // Status indicators only appear on sent messages, not received ones
  const isSentMessage = message.type === "assistant";
  if (message.channel !== "wapp" || !message.wappStatus || !isSentMessage) {
    return null;
  }

  const status = message.wappStatus;
  const iconSize = "h-3 w-3 lg:h-4 lg:w-4";

  if (status === "failed") {
    return (
      <div className="flex items-center relative group cursor-pointer">
        <AlertTriangle className={`${iconSize} text-red-500`} />
        {message.error && (
          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal min-w-[200px] max-w-xs z-10 break-words">
            {message.error}
          </div>
        )}
      </div>
    );
  }

  if (status === "sent") {
    return (
      <div className="flex items-center">
        <Check className={`${iconSize} text-gray-500`} />
      </div>
    );
  }

  if (status === "delivered") {
    return (
      <div className="flex items-center -space-x-1">
        <Check className={`${iconSize} text-gray-500`} />
        <Check className={`${iconSize} text-gray-500`} />
      </div>
    );
  }

  if (status === "read") {
    return (
      <div className="flex items-center -space-x-1">
        <Check className={`${iconSize} text-green-500`} />
        <Check className={`${iconSize} text-green-500`} />
      </div>
    );
  }

  return null;
}

function MessageReplyQuote({
  replyTo,
  tr,
}: {
  replyTo: MessageReplyTo;
  tr: (es: string, en: string) => string;
}) {
  const preview =
    replyTo.content?.trim() ||
    (replyTo.fileUrl
      ? replyTo.fileName || tr("Archivo adjunto", "Attachment")
      : tr("(mensaje)", "(message)"));

  const roleLabel = (() => {
    switch (replyTo.type) {
      case "user":
        return tr("Contacto", "Contact");
      case "assistant":
        return "Eloria";
      case "operator":
        return tr("Respuesta manual", "Manual reply");
      case "manager":
        return tr("Administrador", "Manager");
      case "manager-response":
        return tr("Respuesta del administrador", "Manager response");
      default:
        return tr("Mensaje", "Message");
    }
  })();

  return (
    <div
      className={`mb-2 rounded-md border-l-4 px-2 py-1.5 text-xs ${getReplyQuoteClassName(replyTo.type)}`}
    >
      <div className="font-semibold truncate text-gray-800">{roleLabel}</div>
      <div className="line-clamp-2 break-words text-gray-600">{preview}</div>
    </div>
  );
}

function getReplyWindowSubstituteIndicator(
  message: Message,
  tr: (es: string, en: string) => string,
) {
  const originalContent = message.replyWindowOriginalContent;
  if (!originalContent) {
    return null;
  }

  const iconSize = "h-3 w-3 lg:h-4 lg:w-4";
  const explanation = tr(
    "Como el contacto no escribió en las últimas 24 horas, WhatsApp solo permite enviar este tipo de mensajes de plantilla. Cuando responda, Eloria le enviará este mensaje completo.",
    "Because the contact hasn't written in the last 24 hours, WhatsApp only allows this type of template message. When they reply, Eloria will send them this full message.",
  );

  return (
    <div className="flex items-center relative group cursor-pointer">
      <Info className={`${iconSize} text-blue-500`} />
      <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto min-w-[200px] max-w-xs z-10">
        <p className="text-sm font-medium whitespace-pre-wrap break-words max-h-40 overflow-y-auto overscroll-contain">
          {originalContent}
        </p>
        <p className="mt-2 text-[10px] text-gray-400 leading-snug">{explanation}</p>
      </div>
    </div>
  );
}
